import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import AIFileUploadSection from './AIFileUploadSection';
import { UploadedFile } from './types';
import AIProcessingState from './AIProcessingState';
import AISuccessState from './AISuccessState';
import AIFailureState from './AIFailureState';
import AITerminatedState from './AITerminatedState';
import AILastUpdatedTime from './AILastUpdatedTime';
import AIHelpFooter from './AIHelpFooter';
import AIDeleteSuccessToast from './AIDeleteSuccessToast';
import AIGapSummary from './GapSummary/AIGapSummary';
import { useLocalizer } from './hooks/useLocalizer';
import { useAIOperationMonitor } from './hooks/useAIOperationMonitor';
import { useErrorMessage } from './hooks/useErrorMessage';
import { useFileMetadataStorage } from './hooks/useFileMetadataStorage';
import { clearStoredOverridePreference } from './hooks/useOverrideStorage';
import './style.css';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { DataCollectionAiAgentOperationType } from '../../../store/Models/Surveys/SurveyDataModel';
import { ApplicationState } from '../../../store';
import {
    isSurveyReadonly,
    SupplierResponseAlreadyProvidedResult,
    SurveyResponseSubmittedOfflineImportUnavailableMessage
} from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import { getActiveAIOperationFromState, shouldShowOfflineGapAnalysisCta } from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import { showSurveyResponseSubmittedOfflineImportUnavailablePopup } from '../../Survey/SurveyResponseSubmittedOfflineImportUnavailableModal';
import { SupplierGapDocumentUploadDetails } from '../../../store/Models/Api/DataCollectionGapSummaryModels';

interface SupplierAiAgentProps {
    requestId?: string;
    localizer: IStringLocalizer;
    operationType: DataCollectionAiAgentOperationType;
    onNextPage?: () => void;
}

let isDeleteToastDismissedForCurrentTab = false;

const getSupplierReprocessingStorageKey = (
    requestId: string,
    operationType: DataCollectionAiAgentOperationType
): string => (
    `ai_supplier_reprocessing_operation_${requestId}_${DataCollectionAiAgentOperationType[operationType]}`
);

const markSupplierReprocessingOperation = (
    requestId: string | undefined,
    operationType: DataCollectionAiAgentOperationType,
    operationId: string | null | undefined
): void => {
    if (!requestId || !operationId) {
        return;
    }

    localStorage.setItem(
        getSupplierReprocessingStorageKey(requestId, operationType),
        operationId
    );
};

const isSupplierReprocessingOperation = (
    requestId: string | undefined,
    operationType: DataCollectionAiAgentOperationType,
    operationId: string | null | undefined
): boolean => {
    if (!requestId || !operationId) {
        return false;
    }

    return localStorage.getItem(
        getSupplierReprocessingStorageKey(requestId, operationType)
    ) === operationId;
};

/**
 * SupplierAiAgent Component
 * 
 * Manages AI-powered document processing for survey responses.
 * Uses the centralized AISurveyAgentController for all AI operations.
 * 
 * Key Features:
 * - File upload and management
 * - AI workflow triggering via AISurveyAgentController
 * - Real-time processing status updates from Redux via AIOperationMonitor hook
 */
export const SupplierAiAgent: React.FC<SupplierAiAgentProps> = ({
    localizer,
    requestId,
    operationType,
    onNextPage
}) => {
    // Local component state
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [showDeleteToast, setShowDeleteToast] = useState(false);
    const [showGapSummary, setShowGapSummary] = useState(false);
    const submittedResponseTerminationPopupOperationIdRef = useRef<string | null>(null);
    const localize = useLocalizer(localizer);

    // Auto-persist file metadata to localStorage and restore on mount
    const { clearMetadata } = useFileMetadataStorage(requestId, operationType, uploadedFiles, setUploadedFiles);

    /**
     * Initializes Bootstrap popovers for accessibility tooltips
     */
    const registerPopovers = (popoverIds: string[]) => {
        for (let i = 0; i < popoverIds.length; i++) {
            let popover = $(`#${popoverIds[i]}`);
            if (popover.length > 0 && typeof popover.popover === 'function') {
                popover.popover("destroy");
                popover.popover({
                    trigger: "hover focus"
                });
            }
        }
    };

    // Error message management via custom hook
    const { errorMessage, errorMessageRef, setError, clearError } = useErrorMessage();

    // Monitor AI operation state changes via custom hook
    const aiOperationState = useAIOperationMonitor(
        {},
        localizer,
        operationType
    );

    const {
        processingProgress,
        currentStage,
        hasFailed,
        failureMessage,
        isTerminated,
        isProcessing,
        hasAIAnswers,
        isDeleteCompleted,
        isDeleteInProgress,
        isActivelyProcessing,
        agentState
    } = aiOperationState;

    // Get AI operation from Redux to extract operationId
    const aiOperation = useSelector((state: ApplicationState) =>
        getActiveAIOperationFromState(state.aiSurveyAgentState, operationType)
    );
    const isReprocessingSupplierDocuments = isSupplierReprocessingOperation(
        requestId,
        operationType,
        aiOperation?.operationId
    );
    const responseId = useSelector((state: ApplicationState) =>
        state.surveyResponseState?.responseId
    );
    // Get core survey data so we can derive readonly state (submitted survey
    // and AI-processing readonly states — see isSurveyReadonly). Used to disable
    // post-submission actions like "Upload Different Files" / "Delete All AI Answers".
    const coreData = useSelector((state: ApplicationState) =>
        state.surveyResponseState?.coreData ?? null
    );
    const isReadonly = isSurveyReadonly(coreData, aiOperation ?? null);
    const isWorkflowInProgress = isProcessing || isActivelyProcessing || isDeleteInProgress;
    const shouldShowLastUpdated = isWorkflowInProgress && !hasFailed;
    const shouldShowGapAnalysisCta = shouldShowOfflineGapAnalysisCta(operationType, aiOperation);

    const handleSuccessNextPage = () => {
        if (shouldShowGapAnalysisCta && requestId) {
            setShowGapSummary(true);
            return;
        }

        onNextPage?.();
    };

    const openFileUploadSection = (files: UploadedFile[]) => {
        clearError();
        clearMetadata();
        setUploadedFiles(files);
        setShowGapSummary(false);
    };

    const handleGapSummaryStartOver = () => {
        openFileUploadSection([]);
    };

    const handleProcessSupplierDocuments = (
        supplierFiles: SupplierGapDocumentUploadDetails[]
    ) => {
        markSupplierReprocessingOperation(
            requestId,
            operationType,
            aiOperation?.operationId
        );
        clearStoredOverridePreference(responseId);
        openFileUploadSection(supplierFiles.map(file => ({
            id: file.fileId,
            fileId: file.fileId,
            file: new File([], file.fileName, {
                type: file.contentType || 'application/octet-stream'
            }),
            fileName: file.fileName,
            fileSize: file.fileSize,
            isUploading: false
        })));
    };

    useEffect(() => {
        if (!hasAIAnswers) {
            setShowGapSummary(false);
        }
    }, [hasAIAnswers]);

    useEffect(() => {
        setShowGapSummary(
            !!requestId
            && !!aiOperation?.gapSummary?.dateSent
            && !isReprocessingSupplierDocuments
        );
    }, [
        aiOperation?.gapSummary?.dateSent,
        aiOperation?.operationId,
        isReprocessingSupplierDocuments,
        requestId
    ]);

    useEffect(() => {
        if (!requestId || !aiOperation?.operationId) {
            return;
        }

        const storageKey = getSupplierReprocessingStorageKey(requestId, operationType);
        const storedOperationId = localStorage.getItem(storageKey);
        if (storedOperationId && storedOperationId !== aiOperation.operationId) {
            localStorage.removeItem(storageKey);
        }
    }, [aiOperation?.operationId, operationType, requestId]);

    useEffect(() => {
        if (operationType !== DataCollectionAiAgentOperationType.OfflineResponse) {
            return;
        }

        if (!hasFailed || !isTerminated) {
            return;
        }

        if (aiOperation?.metadata?.terminationReason !== SupplierResponseAlreadyProvidedResult) {
            return;
        }

        const operationId = aiOperation?.operationId ?? "";
        if (submittedResponseTerminationPopupOperationIdRef.current === operationId) {
            return;
        }

        submittedResponseTerminationPopupOperationIdRef.current = operationId;
        if (!showSurveyResponseSubmittedOfflineImportUnavailablePopup()) {
            setError(localize(SurveyResponseSubmittedOfflineImportUnavailableMessage));
        }
    }, [aiOperation?.metadata?.terminationReason, aiOperation?.operationId, hasFailed, isTerminated, localize, operationType, setError]);

    useEffect(() => {
        if (!isDeleteCompleted) {
            return;
        }

        if (isDeleteToastDismissedForCurrentTab) {
            return;
        }

        setShowDeleteToast(true);

        const timeoutId = window.setTimeout(() => {
            setShowDeleteToast(false);
        }, 3000);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [isDeleteCompleted]);

    if (showGapSummary && shouldShowGapAnalysisCta && requestId) {
        return (
            <AIGapSummary
                requestId={requestId}
                operationId={aiOperation?.operationId ?? undefined}
                gapSummary={aiOperation?.gapSummary}
                onStartOver={handleGapSummaryStartOver}
                onProcessSupplierDocuments={handleProcessSupplierDocuments}
            />
        );
    }

    const aiFileParserClassName = [
        'ai-file-parser-container',
        operationType === DataCollectionAiAgentOperationType.OfflineResponse ? 'offline-response' : '',
        shouldShowLastUpdated ? 'with-last-updated' : ''
    ].filter(Boolean).join(' ');

    return (
        <div className={aiFileParserClassName}>
            {/* Error Message Display */}
            {errorMessage && !hasFailed && (
                <div
                    ref={errorMessageRef}
                    className="alert alert-danger"
                    style={{ marginBottom: '20px' }}
                    tabIndex={-1}
                    role="alert"
                    aria-live="polite"
                >
                    {errorMessage}
                </div>
            )}

            {/* Failure State Section */}
            {hasFailed && !isTerminated && (
                <AIFailureState
                    requestId={requestId}
                    operationId={aiOperation?.operationId || undefined}
                    uploadedFiles={uploadedFiles}
                    operationType={operationType}
                    onClearError={clearError}
                    onError={setError}
                    localizer={localizer}
                    errorMessage={failureMessage || undefined}
                />
            )}

            {hasFailed && isTerminated && (
                <AITerminatedState
                    requestId={requestId}
                    operationId={aiOperation?.operationId || undefined}
                    uploadedFiles={uploadedFiles}
                    operationType={operationType}
                    onClearError={clearError}
                    onError={setError}
                    localizer={localizer}
                    errorMessage={failureMessage || undefined}
                />
            )}

            {/* File Upload Section - Hide when actively processing or has answers */}
            {(!hasAIAnswers || isReprocessingSupplierDocuments)
                && !hasFailed
                && !isWorkflowInProgress && (
                <AIFileUploadSection
                    requestId={requestId}
                    uploadedFiles={uploadedFiles}
                    isProcessing={isWorkflowInProgress}
                    hasAIAnswers={!!hasAIAnswers && !isReprocessingSupplierDocuments}
                    onUploadedFilesChange={setUploadedFiles}
                    onError={setError}
                    localizer={localizer}
                    registerPopoversCb={registerPopovers}
                    operationType={operationType}
                />
            )}

            {/* Processing State Section - Show when actively processing */}
            <AIProcessingState
                    isProcessing={isWorkflowInProgress}
                    processingProgress={processingProgress}
                    currentStage={currentStage}
                    localizer={localizer}
                    onError={setError}
                    operationType={operationType}
                />

            {/* Success State Section */}
            <AISuccessState
                hasAIAnswers={!!hasAIAnswers && !isReprocessingSupplierDocuments}
                isProcessing={isWorkflowInProgress}
                isDeleteCompleted={isDeleteCompleted}
                uploadedFiles={uploadedFiles}
                onError={setError}
                localizer={localizer}
                agentState={agentState}
                onNextPage={handleSuccessNextPage}
                isReadonly={isReadonly}
                operationType={operationType}
            />

            <AIHelpFooter localizer={localizer} />

            {/* Last Updated Time - Show when processing and not failed */}
            {shouldShowLastUpdated && 
                <AILastUpdatedTime
                    localizer={localizer}
                    operationType={operationType}
                />
            }

            <AIDeleteSuccessToast
                show={showDeleteToast}
                title={localize("AI Answers Deleted")}
                description={localize("All AI-generated answers have been removed from your survey.")}
                onClose={() => {
                    isDeleteToastDismissedForCurrentTab = true;
                    setShowDeleteToast(false);
                }}
            />
        </div>
    );
};

export default SupplierAiAgent;
